import { useCallback } from "react";
import { useMutation, graphql } from "react-relay";
import {
  TodoStatus,
  useTodoSetStatusMutation,
} from "./__generated__/useTodoSetStatusMutation.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { setOrdinalMap } from "arkh";

export const useTodoSetStatus = (todoId: string | null) => {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [updateStatus] = useMutation<useTodoSetStatusMutation>(graphql`
    mutation useTodoSetStatusMutation(
      $todoId: ID!
      $newStatus: TodoStatus!
      $employeeId: ID
    ) @raw_response_type {
      todo(todoId: $todoId, employeeId: $employeeId) {
        updateTodoItemStatus(input: { status: $newStatus }) {
          todo {
            id
            status
            ordinal
            parent {
              id
              status
              canChangeStatus
            }
            canChangeStatus
            subTodos {
              status
              canChangeStatus
              ...TodoListItemCompletedInfo_todo
            }
            linkedProducts {
              id
              numberOfPendingTodos
            }
            lastChangedStatusByActor {
              ...Avatar_actor
            }
            ...TodoListItemCompletedInfo_todo
            timeToResolvedFormatted
            ...TodoEditTimeline_todo @defer
          }
        }
      }
    }
  `);

  const cb = useCallback(
    (newStatus: TodoStatus) => {
      if (!todoId) {
        return;
      }

      updateStatus({
        optimisticUpdater: (store) => {
          store.get(todoId)?.setValue(newStatus, "status");
        },
        variables: {
          todoId,
          newStatus,
          employeeId: currentEmployeeId,
        },
      });
    },
    [todoId, updateStatus, currentEmployeeId]
  );
  return cb;
};

export function useTodoSetStatusGlobalyDebounced(todoId: string) {
  const updateStatus = useTodoSetStatus(todoId);

  return useCallback(
    async (newStatus: TodoStatus, currentOrdinal: string) => {
      setOrdinalMap(todoId, currentOrdinal);
      updateStatus(newStatus);
    },
    [todoId, updateStatus]
  );
}
